
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface MagneticButtonProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: () => void;
  disabled?: boolean;
}

export const MagneticButton = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false
}: MagneticButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const variants = {
    primary: 'bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary text-white shadow-2xl hover:shadow-brand-primary/50',
    secondary: 'bg-white/20 backdrop-blur-xl border border-white/30 text-brand-primary shadow-xl hover:shadow-white/20',
    ghost: 'bg-transparent text-brand-primary hover:bg-brand-primary/10'
  };

  const sizes = {
    sm: 'px-6 py-3 text-sm',
    md: 'px-8 py-4 text-base',
    lg: 'px-12 py-6 text-lg',
    xl: 'px-16 py-8 text-xl'
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || disabled) return;
    
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    setPosition({ x: x * 0.3, y: y * 0.3 });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.button
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-2xl font-semibold transition-all duration-300 transform-gpu',
        'before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/20 before:to-transparent before:opacity-0 before:transition-opacity before:duration-300',
        'hover:before:opacity-100 hover:scale-105 active:scale-95',
        variants[variant],
        sizes[size],
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      animate={{
        x: position.x,
        y: position.y
      }}
      transition={{
        type: 'spring',
        damping: 20,
        stiffness: 300,
        mass: 0.5
      }}
      whileHover={{
        scale: disabled ? 1 : 1.05,
        rotateX: disabled ? 0 : 5,
        rotateY: disabled ? 0 : position.x * 0.1,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      disabled={disabled}
      style={{
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
    >
      <motion.div
        className="relative z-10 flex items-center justify-center gap-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
      
      {/* Liquid effect background */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-accent opacity-0 rounded-2xl"
        whileHover={{ opacity: 0.2 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Ripple effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%)',
          transform: `translate(${position.x * 0.5}px, ${position.y * 0.5}px)`
        }}
        animate={{
          scale: [0, 1],
          opacity: [0.5, 0]
        }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          ease: 'easeOut'
        }}
      />
    </motion.button>
  );
};
